import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import i18n from 'scripts/i18n/i18n';

import connect from 'components/lib/connect';
import Button, { ButtonTypes } from 'components/common/button';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import FilterAnswerPerformance from 'actions/answer_performance/filter_answer_performance';
import NavigateTo from 'actions/current_location/navigate_to';
import PerformanceDateRange from 'models/performance_date_range';
import Reports from 'models/location/reports';

export function AnswerPerformanceFilters({
  audiences,
  onChangeFilter,
  onOpenConversationReport,
  selectedAudienceId,
  selectedDateRange,
  enableConversationReport,
}) {
  const { t } = useTranslation();
  const [audienceId, setAudience] = useState(selectedAudienceId || '');
  const [dateRange, setDateRange] = useState(selectedDateRange || PerformanceDateRange.LAST_1_DAY);

  const audienceText = audiences.find(o => o.value === audienceId)?.label;
  const onChangeAudienceFilter = useCallback(
    newAudienceId => {
      onChangeFilter({ audienceId: newAudienceId, dateRange });
      setAudience(newAudienceId);
    },
    [dateRange, onChangeFilter]
  );

  const dateRangeText = getDateRangeOptions().find(o => o.value === dateRange)?.label;
  const onChangeDateRangeFilter = useCallback(
    newDateRange => {
      onChangeFilter({ audienceId, dateRange: newDateRange });
      setDateRange(newDateRange);
    },
    [audienceId, onChangeFilter]
  );

  return (
    <ControlWrapper>
      <Filters>
        <StyledDropdownWrapper>
          <DropdownMenu
            className="date-range-filter"
            onSelect={onChangeDateRangeFilter}
            options={getDateRangeOptions()}
            text={dateRangeText}
          />
        </StyledDropdownWrapper>
        {!!audiences.length && (
          <StyledDropdownWrapper>
            <DropdownMenu
              className="audience-filter"
              onSelect={onChangeAudienceFilter}
              options={audiences}
              text={audienceText}
            />
          </StyledDropdownWrapper>
        )}
      </Filters>
      {enableConversationReport && (
        <StyledButtonWrapper
          data-aid="answerPerformanceFilters-openConversationReport"
          onClick={onOpenConversationReport}
          type={ButtonTypes.SECONDARY}
        >
          {t('Open Conversation Report')}
        </StyledButtonWrapper>
      )}
    </ControlWrapper>
  );
}

AnswerPerformanceFilters.propTypes = {
  audiences: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  enableConversationReport: PropTypes.bool,
  onChangeFilter: PropTypes.func,
  onOpenConversationReport: PropTypes.func,
  selectedAudienceId: PropTypes.string,
  selectedDateRange: PropTypes.string,
};

AnswerPerformanceFilters.defaultProps = { enableConversationReport: false };

export const Filters = styled.div`
  display: flex;
  padding: ${p => p.theme.spacing.medium} 0;
`;

export const ControlWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const StyledButtonWrapper = styled(Button)`
  flex-shrink: 0;
  height: fit-content;
`;

const StyledDropdownWrapper = styled.div`
  margin-right: ${p => p.theme.spacing.inlineMedium};
`;

// Evaluated per render (not module scope) so labels reflect the active language.
const getDateRangeOptions = () => [
  { label: i18n.t('Last 1 day'), value: PerformanceDateRange.LAST_1_DAY },
  { label: i18n.t('Last 7 days'), value: PerformanceDateRange.LAST_7_DAYS },
  { label: i18n.t('Last 30 days'), value: PerformanceDateRange.LAST_30_DAYS },
  { label: i18n.t('Last 90 days'), value: PerformanceDateRange.LAST_90_DAYS },
];

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const enableConversationReport = isFeatureEnabled('viewAnswerPerformance');
  const audiences = getProvider('audiences')
    .findAll()
    .map(audience => ({
      value: audience.id,
      label: audience.name,
    }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  if (audiences?.length) {
    audiences.unshift({ label: i18n.t('All Audiences'), value: '' });
  }

  const location = getProvider('currentLocation').get();

  return {
    audiences,
    enableConversationReport,
    selectedAudienceId: location.audienceId,
    selectedDateRange: location.dateRange,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onChangeFilter: ({ audienceId, dateRange }) => {
      executeAction(FilterAnswerPerformance, { audienceId, dateRange });
    },
    onOpenConversationReport: () => {
      executeAction(NavigateTo, Reports.create({ slug: 'sidekick-answer-performance-detail' }));
    },
  };
}

const AnswerPerformanceFiltersContainer = connect(mapStateToProps, mapExecuteToProps)(AnswerPerformanceFilters);

export default AnswerPerformanceFiltersContainer;
